import { Component, ComponentFactoryResolver, Inject, NgZone, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AccessManagerResourceActionType } from "../auth/accessManager/accessManager.models";
import { AccessManagerService } from '../auth/accessManager/accessManager.service';
import { INamedClass } from '../common/iNamedClass';
import { AppGroup } from '../common/launcher/appGroup';
import { LauncherComponent } from '../common/launcher/launcher.component';
import { LoadManagerService } from '../common/loadingOverlay/loadManager.service';
import { LoggerService } from '../common/logging/logger.service';
import { OfficeSelectorService } from '../common/officeSelector/officeSelector.service';
import { DashboardTour, DashboardTourOptions } from '../tour/dashboard.tour';
import { TourService } from '../tour/tour.service';
import { BrowserUtilsService } from '../util/browserUtils.service';
import { DashboardCfgService } from './dashboardCfg.service';
import { StaticDashboardDefinitionComponent } from './staticDashboardDefinition.component';
import { FeaturePermissionsService } from '../auth/raas/featurePermissions.service';
import { ImpersonationMenuComponent } from '../common/impersonationMenu/impersonationMenu.component';
import { DashboardMenuTabSelectorService } from '../dashboardCommon/services/dashboardMenuTabSelector.service';
import { TabEntry } from '../dashboardCommon/tabEntry';
import { ImpersonationService } from '../auth/apigee/impersonation.service';
import { ApigeeService } from '../auth/apigee/apigee.service';

@Component({
    selector: 'dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    providers: [LoadManagerService],
    standalone: false
})
export class DashboardComponent implements OnInit, INamedClass {
    public getClassName() { return "DashboardComponent"; }

    public tabEntries: TabEntry[] = [
        new TabEntry("callToActionMenuOption", "Call to Action"),
        new TabEntry("clientesMenuOption", "Clientes"),
        new TabEntry("talentoMenuOption", "Talento"),
        new TabEntry("gestionControlMenuOption", "Gestión & Control")
    ];

    private mBusy: boolean;
    private mIsProfessionals: boolean = false;
    private mLoadStatusUpdatedSubscription: Subscription;

    @ViewChild('dashboardItemContainer', { read: ViewContainerRef }) private itemContainer: ViewContainerRef;
    @ViewChild(LauncherComponent) private launcher: LauncherComponent;
    @ViewChild(ImpersonationMenuComponent) private impersonationMenu: ImpersonationMenuComponent;

    private staticDashboardDefinition: StaticDashboardDefinitionComponent;

    public get isProfessionals(): boolean {
        return this.officeSelectorService.selectedOffice && !this.officeSelectorService.selectedOffice.isTEA && this.mIsProfessionals;
    }

    public get isUPayroll(): boolean {
        return this.officeSelectorService.selectedOffice && this.officeSelectorService.selectedOffice.isUPayroll;
    };

    public get isFundation(): boolean {
        return this.officeSelectorService.selectedOffice && this.officeSelectorService.selectedOffice.isFundation;
    };

    public get selectedOfficeIsRecruitment(): boolean {
        return this.officeSelectorService.selectedOffice && this.officeSelectorService.selectedOffice.regionId == "70"
    };

    public get showDasboardRecruitment(): boolean {
        return this.dashboardCfgService.getCfg().showDasboardRecruitment == 1
    };

    private mAccessGranted: Promise<boolean>;
    private mSelectedTab: number;
    private mSelectedTabChangedSubscription: Subscription;
    public get selectedTab(): number { return this.mSelectedTab; }

    private mSelectedOfficeUpdatedSubscription: Subscription;
    public showKpis: boolean = true;

    private routerEventsSubscription: Subscription;

    constructor(private dashboardCfgService: DashboardCfgService, private viewContainerRef: ViewContainerRef,
        private factoryResolver: ComponentFactoryResolver,
        private zone: NgZone, private router: Router,
        private logger: LoggerService,
        private apigeeService: ApigeeService,
        private impersonationService: ImpersonationService,
        private accessManagerService: AccessManagerService,
        private loadManagerService: LoadManagerService,
        @Inject(DashboardMenuTabSelectorService) private selectedTabService: DashboardMenuTabSelectorService,
        private officeSelectorService: OfficeSelectorService,
        private tourService: TourService,
        private activatedRoute: ActivatedRoute,
        private browserUtilsService: BrowserUtilsService,
        private featurePermissionsService: FeaturePermissionsService) {
    }

    public get busy(): boolean {
        return this.mBusy;
    }

    public get impersonationEnabled(): boolean {
        return this.impersonationService.canImpersonate;
    }

    public async ngOnInit(): Promise<void> {
        this.routerEventsSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.checkTour();
            }
        });

        this.mLoadStatusUpdatedSubscription = this.loadManagerService.loadStatusUpdated.subscribe((value) => {
            this.zone.run(() => this.mBusy = value);
        });

        this.mSelectedTabChangedSubscription = this.selectedTabService.selectedTabChanged.subscribe((index) => this.mSelectedTab = index);
        this.mSelectedTab = this.selectedTabService.selectedTab;

        this.mSelectedOfficeUpdatedSubscription = this.officeSelectorService.selectedOfficeUpdated.subscribe((value) => {
            this.zone.run(() => {
                this.showKpis = value ? value.isTEA : false;

                if ((this.mIsProfessionals && !(value ? value.isTEA : false)) || this.isFundation) {
                    this.zone.run(() => this.router.navigate(['home']));
                }
                else if (this.isUPayroll) {
                    this.zone.run(() => this.router.navigate(['dashboard/direct']));
                }
                else if (this.selectedOfficeIsRecruitment && this.showDasboardRecruitment) {
                    this.zone.run(() => this.router.navigate(['dashboard/recruitment']));
                }
            });
        });

        this.loadManagerService.incLoadRequest();
    }

    public async ngAfterViewInit(): Promise<void> {
        try {
            this.mAccessGranted = new Promise<boolean>(async (resolve, reject) => {
                try {
                    let apigee = this.apigeeService;

                    if (apigee.apiToken == null && (apigee.googleAuthToken == null || apigee.googleDirectoryToken == null)) {
                        throw new Error("No credentials present");
                    }
                    else {
                        await this.featurePermissionsService.professionalsEnabled().then(result => this.zone.run(() => this.mIsProfessionals = result));

                        let permissions = await this.accessManagerService.getResourcePermissions(this.dashboardCfgService.getCfg().resourceId);
                        if (permissions && permissions.getResourceAction(AccessManagerResourceActionType.ApplicationAccess)) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    }
                } catch (error) {
                    reject(error);
                }
            });

            if (!await this.mAccessGranted) {
                this.zone.run(() => this.router.navigate(['home']));
            } else {
                setTimeout(() => {
                    if (this.officeSelectorService.selectedOffice && !this.officeSelectorService.selectedOffice.isTEA) {
                        if (this.mIsProfessionals || this.isFundation) this.zone.run(() => this.router.navigate(['home']));
                        else this.zone.run(() => this.showKpis = false);
                    }
                    else if (this.officeSelectorService.selectedOffice && this.isUPayroll) {
                        this.zone.run(() => this.router.navigate(['dashboard/direct']));
                    }
                    else if (this.selectedOfficeIsRecruitment && this.showDasboardRecruitment) {
                        this.zone.run(() => this.router.navigate(['dashboard/recruitment']));
                    } else {
                        this.zone.run(() => this.showKpis = true);
                    }
                }, 250);
            }

        } catch (error) {
            this.zone.run(() => this.router.navigate(['login']));
        }
        finally {
            this.loadManagerService.decLoadRequest();
        }


        try {
            if (await this.mAccessGranted) {
                this.logger.traceActivity("Acceso a Dashboard");
                await this.loadDashboardItems();

                this.checkTour();
            }
        } catch (error) { }
    }

    private async loadDashboardItems(): Promise<void> {
        try {
            this.loadManagerService.incLoadRequest();
            let factory = this.factoryResolver.resolveComponentFactory(StaticDashboardDefinitionComponent);
            this.staticDashboardDefinition = this.itemContainer.createComponent(factory).instance;
        } catch (error) {
            throw error;
        } finally {
            this.loadManagerService.decLoadRequest();
        }
    }

    public ngOnDestroy(): void {
        if (this.routerEventsSubscription) this.routerEventsSubscription.unsubscribe();
        if (this.mSelectedOfficeUpdatedSubscription) this.mSelectedOfficeUpdatedSubscription.unsubscribe();
        if (this.mLoadStatusUpdatedSubscription) this.mLoadStatusUpdatedSubscription.unsubscribe();
        if (this.mSelectedTabChangedSubscription) this.mSelectedTabChangedSubscription.unsubscribe();
    }

    private checkTour() {

        let isOfficeSelected: boolean = this.officeSelectorService.selectedOffice != null;

        if (isOfficeSelected) {
            let forceTour: boolean = (this.activatedRoute.snapshot.data && this.activatedRoute.snapshot.data["forceTour"]) ||
                (this.activatedRoute.routeConfig.data && this.activatedRoute.routeConfig.data["forceTour"]);

            if (forceTour) {
                let isTourUrl: boolean = this.activatedRoute.snapshot && this.activatedRoute.snapshot.url && this.activatedRoute.snapshot.url.find((segment) => segment.path == "tour") != null;

                this.router.config.find(x => x.path == "dashboard").data = {};

                new DashboardTour(new DashboardTourOptions(
                    this.impersonationEnabled,
                    this.tourService,
                    this.browserUtilsService,
                    async (event) => {
                        window.scrollTo(0, 0);

                        switch (event.sourceStep) {
                            case DashboardTour.LAUNCHER_STEP: {
                                this.launcher.sideBarMouseLeave();

                                this.launcher.closeAppMenu();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                            case DashboardTour.TALENT_APP_MENU_STEP: {
                                break;
                            }
                            case DashboardTour.GENERIC_KPI_UPDATE_STEP: {
                                this.staticDashboardDefinition.tourKpi.enableMenuAutoClose();
                                this.staticDashboardDefinition.tourKpi.closeMenu();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }

                            case DashboardTour.OM_INTRO: {
                                this.impersonationMenu.enableAutoClose();
                                this.impersonationMenu.closeDropdown();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                        }

                        switch (event.destinationStep) {
                            case DashboardTour.WELCOME_STEP: {
                                this.selectedTabService.selectedTab = 0;
                                break;
                            }
                            case DashboardTour.LAUNCHER_STEP: {
                                this.launcher.sideBarMouseEnter();
                                break;
                            }
                            case DashboardTour.TALENT_APP_MENU_STEP: {
                                this.launcher.openAppMenu("FBI Talento", AppGroup.FBITalent);
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                            case DashboardTour.GENERIC_KPI_UPDATE_STEP: {
                                this.staticDashboardDefinition.tourKpi.disableMenuAutoClose();
                                this.staticDashboardDefinition.tourKpi.openMenu();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }

                            case DashboardTour.OM_HIGHLIGHT: {
                                this.impersonationMenu.disableAutoClose();
                                this.impersonationMenu.openDropdown();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                        }
                    },
                    async (event) => {
                        window.scrollTo(0, 0);

                        switch (event.sourceStep) {
                            case DashboardTour.LAUNCHER_STEP: {
                                this.launcher.sideBarMouseLeave();
                                break;
                            }
                            case DashboardTour.TALENT_APP_MENU_STEP: {
                                this.launcher.closeAppMenu();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                            case DashboardTour.GENERIC_KPI_UPDATE_STEP: {
                                this.staticDashboardDefinition.tourKpi.enableMenuAutoClose();
                                this.staticDashboardDefinition.tourKpi.closeMenu();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }

                            case DashboardTour.OM_HIGHLIGHT: {
                                this.impersonationMenu.enableAutoClose();
                                this.impersonationMenu.closeDropdown();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                        }

                        switch (event.destinationStep) {
                            case DashboardTour.WELCOME_STEP: {
                                this.selectedTabService.selectedTab = 0;
                                break;
                            }
                            case DashboardTour.LAUNCHER_STEP: {
                                this.launcher.sideBarMouseEnter();
                                this.launcher.openAppMenu("FBI Talento", AppGroup.FBITalent);
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                            case DashboardTour.TALENT_APP_MENU_STEP: {
                                break;
                            }
                            case DashboardTour.GENERIC_KPI_UPDATE_STEP: {
                                this.staticDashboardDefinition.tourKpi.disableMenuAutoClose();
                                this.staticDashboardDefinition.tourKpi.openMenu();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }

                            case DashboardTour.OM_INTRO: {
                                this.impersonationMenu.disableAutoClose();
                                this.impersonationMenu.openDropdown();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                        }
                    },
                    async (event) => {
                        switch (event.sourceStep) {
                            case DashboardTour.LAUNCHER_STEP: {
                                this.launcher.sideBarMouseLeave();
                                break;
                            }
                            case DashboardTour.TALENT_APP_MENU_STEP: {
                                this.launcher.closeAppMenu();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                            case DashboardTour.GENERIC_KPI_UPDATE_STEP: {
                                this.staticDashboardDefinition.tourKpi.enableMenuAutoClose();
                                this.staticDashboardDefinition.tourKpi.closeMenu();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }

                            case DashboardTour.OM_INTRO:
                            case DashboardTour.OM_HIGHLIGHT: {
                                this.impersonationMenu.enableAutoClose();
                                this.impersonationMenu.closeDropdown();
                                await new Promise<void>((resolve) => setTimeout(resolve, 500));
                                break;
                            }
                        }
                    },
                    forceTour || isTourUrl
                )).start();
            }
        } else {
            let subscription: Subscription = null;
            subscription = this.officeSelectorService.selectedOfficeUpdated.subscribe(() => {
                this.checkTour();
                subscription.unsubscribe();
            });
        }
    }
}
